import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { StaticVideo } from '@/types/video.types';
import { formatMsToHHMMSS } from '@/utils/dates';
import Image from '@ui/image/image';
import { useAppSelector } from '@/store/hooks';
import { selectWorkingProjectImage } from '@/store/slices/project/selectors';

type RecordedVideosGridProps = {
  videos: StaticVideo[] | null;
  selectedVideoIds: Set<string>;
  onToggleSelection: (videoId: string) => void;
};

const RecordedVideosGrid = ({ videos, selectedVideoIds, onToggleSelection }: RecordedVideosGridProps) => {
  const eventImage = useAppSelector(selectWorkingProjectImage);

  if (!videos || videos.length === 0) {
    return (
      <Container className="flex ai-center jc-center padding-v-40">
        <Text className="L2" color="var(--gray-400)">
          No recorded videos available
        </Text>
      </Container>
    );
  }

  return (
    <Container
      className="margin-t-24"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
      }}
    >
      {videos.map((video) => {
        const isSelected = selectedVideoIds.has(video.uuid);
        return (
          <Container
            key={video.uuid}
            className="flex-column gap-8 cursor-pointer"
            onClick={() => onToggleSelection(video.uuid)}
          >
            <Container
              borderRadius="8px"
              aspectRatio="16/9"
              border={isSelected ? '3px solid var(--primary-500)' : '3px solid transparent'}
              className="overflow-hidden position-relative"
            >
              <Image src={video.thumbnail_url} width="100%" height="100%" fallback={eventImage} />
            </Container>

            {/* Video Info */}
            <Container className="flex-column gap-4">
              <Text className="L1" color="var(--gray-100) text-left">
                {video.name || 'Untitled Video'}
              </Text>
              <Text className="C3" color="var(--gray-400) text-left">
                {formatMsToHHMMSS(video.duration_ms)}
              </Text>
            </Container>
          </Container>
        );
      })}
    </Container>
  );
};

export default RecordedVideosGrid;
